import type { ThemeColorName } from '@examdojo/styles';
import type { Value, PointTarget, EdgeTarget, GeometryElementDisplayConfig } from '../../../geometrypad.model';

// =============================================================================
// TYPES
// =============================================================================

/** Triangle construction format (used for explicit format override; auto-detected when omitted) */
export type TriangleFormat = 'SSS' | 'SAS' | 'ASA' | 'AAS' | 'RHS';

/** Canonical triangle property names */
export type TriangleSide = 'a' | 'b' | 'c';
export type TriangleAngle = 'alpha' | 'beta' | 'gamma';
export type TriangleExteriorAngle = 'extAlpha' | 'extBeta' | 'extGamma';
export type TriangleScalarProperty = TriangleSide | TriangleAngle | TriangleExteriorAngle;
export type TriangleVertexName = 'A' | 'B' | 'C';
export type TriangleEdgeName = 'AB' | 'BC' | 'CA';

export interface TriangleElement {
  type: 'Triangle';
  params: {
    // Sides (scalar, length)
    a?: Value;
    b?: Value;
    c?: Value;
    // Interior angles at vertices A, B, C (scalar, degrees)
    alpha?: Value;
    beta?: Value;
    gamma?: Value;
    // Exterior angles (scalar, degrees)
    extAlpha?: Value;
    extBeta?: Value;
    extGamma?: Value;
    // Vertex positions
    A?: PointTarget;
    B?: PointTarget;
    C?: PointTarget;
    // Edge alignments
    AB?: EdgeTarget;
    BC?: EdgeTarget;
    CA?: EdgeTarget;
  };
  names?: {
    a?: string;
    b?: string;
    c?: string;
    alpha?: string;
    beta?: string;
    gamma?: string;
    extAlpha?: string;
    extBeta?: string;
    extGamma?: string;
  };
}

// =============================================================================
// STRUCTURAL CONSTANTS
// =============================================================================

export const TRIANGLE_EDGE_NAMES: readonly string[] = ['AB', 'BC', 'CA'] as const;
export const TRIANGLE_VERTEX_NAMES: readonly string[] = ['A', 'B', 'C'] as const;
export const TRIANGLE_ANGLE_NAMES: readonly TriangleAngle[] = ['alpha', 'beta', 'gamma'] as const;
export const TRIANGLE_PROPERTY_FOR_EDGE: Record<string, TriangleSide> = { AB: 'c', BC: 'a', CA: 'b' };

// =============================================================================
// DISPLAY DEFAULTS
// =============================================================================

/**
 * Default display theme for triangles.
 *
 * Edge colors follow the convention: edge carries the color of the OPPOSITE angle.
 * - AB (side c) → purple (gamma's color)
 * - BC (side a) → sky (alpha's color)
 * - CA (side b) → pink (beta's color)
 *
 * Angle colors:
 * - alpha → sky
 * - beta → pink
 * - gamma → purple
 */
export const TRIANGLE_DEFAULTS: GeometryElementDisplayConfig = {
  polygon: { fillColor: 'sky', fillOpacity: 0.1 },
  line: { strokeWidth: 2, color: 'neutral' },
  angle: { fillOpacity: 0.2, radius: 0.8 },
  point: { visible: false, size: 3 },
  subparts: {
    // Edges
    AB: { line: { color: 'purple' } },
    BC: { line: { color: 'sky' } },
    CA: { line: { color: 'pink' } },
    // Angles
    alpha: { angle: { fillColor: 'sky', strokeColor: 'sky', visible: true } },
    beta: { angle: { fillColor: 'pink', strokeColor: 'pink', visible: true } },
    gamma: { angle: { fillColor: 'purple', strokeColor: 'purple', visible: true } },
    // Points
    A: { point: { color: 'sky' } },
    B: { point: { color: 'pink' } },
    C: { point: { color: 'purple' } },
  },
};

/** @deprecated Use TRIANGLE_DEFAULTS.subparts instead */
export const TRIANGLE_COLORS: Record<string, ThemeColorName> = {
  alpha: 'sky',
  beta: 'pink',
  gamma: 'purple',
  a: 'sky',
  b: 'pink',
  c: 'purple',
};
